import { zodResolver } from '@hookform/resolvers/zod';
import type { AuthResponse } from '@hudhuria/shared';
import { useMutation } from '@tanstack/react-query';
import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { FieldError, PrimaryButton, RingLogo, TextField } from '~/components/ui';
import { api, ok } from '~/lib/api';
import { completeSignIn } from '~/lib/auth';
import { toast } from '~/lib/toast';

export const Route = createFileRoute('/reset-password')({
  validateSearch: z.object({ token: z.string().catch('') }),
  component: Reset,
});

const schema = z
  .object({ password: z.string().min(8, 'At least 8 characters').max(200), confirm: z.string() })
  .refine((v) => v.password === v.confirm, { message: "Passwords don't match", path: ['confirm'] });
type Values = z.infer<typeof schema>;

function Reset() {
  const navigate = useNavigate();
  const { token } = Route.useSearch();
  const form = useForm<Values>({
    resolver: zodResolver(schema),
    defaultValues: { password: '', confirm: '' },
  });
  const reset = useMutation({
    mutationFn: (v: Values) =>
      ok<AuthResponse>(api.auth.reset.$post({ json: { token, password: v.password } })),
    onSuccess: (res) => {
      toast('Password updated');
      void navigate({ to: completeSignIn(res), replace: true });
    },
    onError: (e) => form.setError('root', { message: e.message }),
  });
  const { errors } = form.formState;

  return (
    <form
      onSubmit={form.handleSubmit((v) => reset.mutate(v))}
      className="flex flex-1 animate-fade-in flex-col px-[26px] pt-safe-10 pb-safe"
      noValidate
    >
      <RingLogo size={54} />
      <div className="mt-[26px] font-display text-[30px] font-bold tracking-[-0.02em]">
        Choose a new password
      </div>
      <div className="mt-2 text-[15px] text-slate-500">
        You'll be signed in on this device, and signed out everywhere else.
      </div>
      <div className="mt-[30px] flex flex-col gap-3">
        <TextField
          label="New password"
          type="password"
          autoComplete="new-password"
          placeholder="At least 8 characters"
          error={errors.password?.message}
          {...form.register('password')}
        />
        <TextField
          label="Confirm password"
          type="password"
          autoComplete="new-password"
          placeholder="Type it again"
          error={errors.confirm?.message}
          {...form.register('confirm')}
        />
        <FieldError>
          {!token ? 'This reset link is incomplete — request a new one.' : errors.root?.message}
        </FieldError>
      </div>
      <div className="mt-auto flex flex-col gap-3 pt-6">
        <PrimaryButton type="submit" loading={reset.isPending} disabled={!token}>
          Save and sign in
        </PrimaryButton>
        <button
          type="button"
          onClick={() => void navigate({ to: '/forgot-password' })}
          className="cursor-pointer border-none bg-transparent text-[13.5px] font-semibold text-brand"
        >
          Request a new link
        </button>
      </div>
    </form>
  );
}
